// Coupon management redesign (Phase 2 PR 4, 2026-05-15):
//   coupon_template create / edit を担う Sheet (slide-over) UI。
//   既存 TierPresetFormSheet パターン踏襲 (= 軽量 useState + 手動 validation)。
//
// Spec: design/coupon-management-redesign-spec.md §API 契約 §POST /system/coupon-templates
//
// auth: BUSINESS は自分が sponsor の landmark に紐付け不可なら backend が 403 で reject。
//   CMS 側は landmark filter dropdown で BUSINESS にも自身の landmark のみ表示される
//   (= LandmarkStore.list が backend 経由で role filter 済)。

import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { RadioGroup, RadioGroupItem } from '@/components/common/RadioGroup';
import { Textarea } from '@/components/common/Textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/common/Select';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle
} from '@/components/common/Sheet';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';

// クーポン画像 (2026-07-02): クライアント側の事前検証値。
// server 側 (FileService.uploadFile) の許容値と揃える (= 10MB / jpg / png)。
// アップロード前に弾くことでサーバ往復なしの即時エラー表示 (spec R-2)。
const IMAGE_SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png'];
const IMAGE_MAX_SIZE = 10 * 1024 * 1024; // 10MB

// 種別 (= 2026-05-16 redesign で `coupon_templates.is_upgrade_only` に保存):
//   - 'normal'  通常クーポン: shake 1 で配布される clean な base coupon。
//     「レビュー後に切替えるクーポン」 (= upgrade_target_template_id) を設定可。
//   - 'upgrade' アップグレードクーポン: shake 2 (= レビュー後) でのみ配布される target coupon。
//     upgrade_target_template_id は強制 NULL (= chain 禁止)。
//     is_upgrade_only=true で DB に保存され、 backend の通常 shake から除外される。
type CouponTemplateKind = 'normal' | 'upgrade';

// 編集時の種別推定:
//   1) template.is_upgrade_only=true (= 新仕様、 backend で正確に判定済) → upgrade
//   2) upgrade_target_template_id 非 NULL → normal で確定
//   3) 他 template から upgrade target として参照されている → upgrade (= 移行期のフォールバック)
//   4) どちらでもない → normal (= default)
function inferKind(
    template: any | null | undefined,
    allTemplates: any[] | undefined
): CouponTemplateKind {
    if (!template) return 'normal';
    if (template.is_upgrade_only === true) return 'upgrade';
    if (template.upgrade_target_template_id != null) return 'normal';
    const isReferencedByOther = (allTemplates ?? []).some(
        t => t.upgrade_target_template_id != null &&
            Number(t.upgrade_target_template_id) === Number(template.id)
    );
    return isReferencedByOther ? 'upgrade' : 'normal';
}

// コード方式 (= 2026-05-16 redesign で `coupon_templates.code_mode` に保存):
//   - 'unified' (新規・デフォルト): 統一コード AA2026 を配布上限 / 期間内で全 user に配布。
//     coupon_batch_codes 行は生成せず、 distribution_count で atomic increment 管理。
//   - 'unique_per_distribution' (既存): coupon_batch_codes に 1 code 1 row、 atomic claim。
type CouponCodeMode = 'unified' | 'unique_per_distribution';

// fortune select sentinel (= Radix Select は '' 値禁止)。null = 共通プール。
const FORTUNE_COMMON_SENTINEL = '__common__';
// §47: 「いつ使うか」未設定 (= このお店で・いつでも) を Select で扱うための sentinel。
const USE_TIMING_NONE = '__none__';

const FORTUNE_OPTIONS: { value: string, label: string }[] = [
    { value: FORTUNE_COMMON_SENTINEL, label: '共通プール (= fortune 問わず)' },
    { value: '1', label: '大吉' },
    { value: '2', label: '中吉' },
    { value: '3', label: '小吉' },
    { value: '4', label: '末吉' },
    { value: '5', label: '凶' }
];

interface Landmark {
    id: number | string,
    name: string,
    sponsor_user_id?: number | null
}

interface Props {
    open: boolean,
    onOpenChange: (open: boolean) => void,
    mode: 'create' | 'edit',
    template: any | null,
    landmarks: Landmark[],
    defaultLandmarkId: number | null,
    // Phase 3 PR 10 (2026-05-15): 同 sponsor の templates (= upgrade target 候補)。
    // 自分自身 / 別 sponsor は呼び出し側で filter。空配列なら upgrade target select は disabled。
    availableUpgradeTargets?: any[],
    onSuccess?: () => void
}

// HTML <input type='datetime-local'> 用 (YYYY-MM-DDTHH:mm)、空 = null として送る。
// 表示用に DB の ISO datetime を「local 時間表記の YYYY-MM-DDTHH:mm」 に整形する。
function isoToLocalInput(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// datetime-local input ("YYYY-MM-DDTHH:mm") を ISO で送る。空 = null。
function localInputToIso(local: string): string | null {
    if (!local) return null;
    const d = new Date(local);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
}

export default observer(function CouponTemplateFormSheet({
    open, onOpenChange, mode, template, landmarks, defaultLandmarkId,
    availableUpgradeTargets, onSuccess
}: Props) {
    const {
        couponTemplateStore: { create: createTemplate, update: updateTemplate, retranslate: retranslateTemplate, uploadImage }
    } = useStore();

    const [landmarkId, setLandmarkId] = useState<string>('');
    const [fortune, setFortune] = useState<string>(FORTUNE_COMMON_SENTINEL);
    const [name, setName] = useState('');
    const [descJa, setDescJa] = useState('');
    const [descEn, setDescEn] = useState('');
    // 民泊 PoC Step 3 (2026-09-26): 自動翻訳の結果 (英・繁体字・韓国語)。日本語 (name / descJa) を保存すると
    // サーバーが AI で生成する。ここで手直しして保存すれば、日本語が変わらない限りその訳が残る。
    const [nameEn, setNameEn] = useState('');
    const [nameZhTw, setNameZhTw] = useState('');
    const [nameKo, setNameKo] = useState('');
    const [descZhTw, setDescZhTw] = useState('');
    const [descKo, setDescKo] = useState('');
    const [retranslating, setRetranslating] = useState(false);
    const [validFrom, setValidFrom] = useState('');
    const [validTo, setValidTo] = useState('');
    const [isActive, setIsActive] = useState(true);
    // Phase 3 PR 10 (2026-05-15): 焼き直し用 upgrade target template の選択。
    // '' = sentinel for null (= upgrade なし)。
    const [upgradeTargetTemplateId, setUpgradeTargetTemplateId] = useState<string>('');
    const [kind, setKind] = useState<CouponTemplateKind>('normal');
    // 2 段階 shake redesign (2026-05-16、 PR 6): コード方式 state。
    // 新規作成時は U5 で確定の 'unified' を pre-select。 編集時は DB 値を反映。
    const [codeMode, setCodeMode] = useState<CouponCodeMode>('unified');
    const [unifiedCode, setUnifiedCode] = useState<string>('');
    // 2026-07-07 (pitfall batch P1-5): 配布実績 (統一: distribution_count / 個別: total_issued) が
    // 1 件でもある template は code_mode 変更を lock。変更すると在庫管理の整合性が崩れるため。
    const codeModeLocked = mode === 'edit' && (
        Number(template?.distribution_count ?? 0) > 0 ||
        Number(template?.total_issued ?? 0) > 0
    );
    // max_distributions: '' = 無制限、 文字列で持って submit 時に Number 変換。
    const [maxDistributionsMode, setMaxDistributionsMode] = useState<'limited' | 'unlimited'>('limited');
    const [maxDistributions, setMaxDistributions] = useState<string>('100');
    const [submitting, setSubmitting] = useState(false);
    // クーポン画像 (2026-07-02): '' = 未設定 (= enduser は従来の motif 表示)。
    // ファイル選択 → 即時アップロード → 配信 URL を state 保持 → 保存時に image_url で送信。
    const [imageUrl, setImageUrl] = useState<string>('');
    // 民泊 PoC Step 2 (2026-09-26): 宿 QR 経由の抽選での出やすさ (重み)。既定 1、0 = 宿 QR では出さない。
    const [drawWeight, setDrawWeight] = useState<string>('1');
    // 卓上 QR / 宿 QR の出し方・使い方・見出し (2026-09-28 §47)。Spec: design/aircle-demo-coupon-screen-spec.md
    const [facilityMode, setFacilityMode] = useState<'random' | 'always'>('random');
    const [redeemMethod, setRedeemMethod] = useState<'staff' | 'code'>('staff');
    const [redeemUrl, setRedeemUrl] = useState<string>('');
    const [useTiming, setUseTiming] = useState<string>(USE_TIMING_NONE);
    const [reissueLimit, setReissueLimit] = useState<string>('1');
    const [showTiming, setShowTiming] = useState<'pay' | 'order'>('pay');
    const [uploadingImage, setUploadingImage] = useState(false);
    const imageInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open) return;
        if (mode === 'edit' && template) {
            setLandmarkId(template.landmark_id != null ? String(template.landmark_id) : '');
            setFortune(template.fortune != null ? String(template.fortune) : FORTUNE_COMMON_SENTINEL);
            setName(template.name ?? '');
            const i18n = template.description_i18n ?? {};
            setDescJa(typeof i18n === 'object' ? (i18n.ja ?? '') : '');
            setDescEn(typeof i18n === 'object' ? (i18n.en ?? '') : '');
            setDescZhTw(typeof i18n === 'object' ? (i18n['zh-TW'] ?? '') : '');
            setDescKo(typeof i18n === 'object' ? (i18n.ko ?? '') : '');
            const nameI18n = template.name_i18n ?? {};
            setNameEn(nameI18n.en ?? '');
            setNameZhTw(nameI18n['zh-TW'] ?? '');
            setNameKo(nameI18n.ko ?? '');
            setValidFrom(isoToLocalInput(template.valid_from));
            setValidTo(isoToLocalInput(template.valid_to));
            setIsActive(template.is_active !== false);
            setUpgradeTargetTemplateId(
                template.upgrade_target_template_id != null ?
                    String(template.upgrade_target_template_id) :
                    ''
            );
            setKind(inferKind(template, availableUpgradeTargets));
            // 2 段階 shake redesign (2026-05-16、 PR 6): code_mode / unified_code 系を DB 値から反映。
            const dbCodeMode = (template.code_mode === 'unified') ? 'unified' : 'unique_per_distribution';
            setCodeMode(dbCodeMode);
            setUnifiedCode(template.unified_code ?? '');
            if (template.max_distributions == null) {
                setMaxDistributionsMode('unlimited');
                setMaxDistributions('');
            } else {
                setMaxDistributionsMode('limited');
                setMaxDistributions(String(template.max_distributions));
            }
            // クーポン画像 (2026-07-02): DB 値から反映。
            setImageUrl(template.image_url ?? '');
            setDrawWeight(template.draw_weight != null ? String(template.draw_weight) : '1');
            setFacilityMode(template.facility_mode === 'always' ? 'always' : 'random');
            setRedeemMethod(template.redeem_method === 'code' ? 'code' : 'staff');
            setRedeemUrl(template.redeem_url ?? '');
            setUseTiming(template.use_timing ?? USE_TIMING_NONE);
            setReissueLimit(template.reissue_limit != null ? String(template.reissue_limit) : '1');
            setShowTiming(template.show_timing === 'order' ? 'order' : 'pay');
        } else {
            setLandmarkId(defaultLandmarkId != null ? String(defaultLandmarkId) : '');
            setFortune(FORTUNE_COMMON_SENTINEL);
            setName('');
            setDescJa('');
            setDescEn('');
            setDescZhTw('');
            setDescKo('');
            setNameEn('');
            setNameZhTw('');
            setNameKo('');
            setValidFrom('');
            setValidTo('');
            setIsActive(true);
            setUpgradeTargetTemplateId('');
            setKind('normal');
            // 2 段階 shake redesign (2026-05-16、 PR 6): 新規作成時のデフォルトは 'unified' (= U5)。
            setCodeMode('unified');
            setUnifiedCode('');
            setMaxDistributionsMode('limited');
            setMaxDistributions('100');
            setImageUrl('');
            setDrawWeight('1');
            setFacilityMode('random');
            setRedeemMethod('staff');
            setRedeemUrl('');
            setUseTiming(USE_TIMING_NONE);
            setReissueLimit('1');
            setShowTiming('pay');
        }
        // availableUpgradeTargets を deps に入れない: MobX observable 由来で
        // 開いている最中に reference 変化があると form 入力中の state が reset されてしまう。
        // 種別の初期推定は sheet open 時の snapshot のみで十分。
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mode, template, defaultLandmarkId]);

    // クーポン画像 (2026-07-02): ファイル選択 → クライアント検証 → 即時アップロード → プレビュー。
    // 保存 (submit) とは独立して動く (= アップロード失敗しても他フィールドの編集は継続可)。
    const handleImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // 同一ファイルの再選択でも change を発火させるため毎回クリア。
        e.target.value = '';
        if (!file) return;
        if (!IMAGE_SUPPORTED_FORMATS.includes(file.type)) {
            toastify('error', '画像は jpg / png のみ対応しています');
            return;
        }
        if (file.size > IMAGE_MAX_SIZE) {
            toastify('error', '画像サイズは 10MB 以下にしてください');
            return;
        }
        setUploadingImage(true);
        try {
            const res = await flowResult(uploadImage({ file }));
            if (res?.ok && res.data?.path) {
                setImageUrl(res.data.path);
            } else {
                toastify('error', res?.message || '画像のアップロードに失敗しました');
            }
        } finally {
            setUploadingImage(false);
        }
    };

    const handleSubmit = async () => {
        if (mode === 'create' && !landmarkId) {
            toastify('error', 'landmark を選択してください');
            return;
        }
        if (!name.trim()) {
            toastify('error', '名称は必須');
            return;
        }
        if (validFrom && validTo) {
            const f = new Date(validFrom);
            const t = new Date(validTo);
            if (t <= f) {
                toastify('error', '有効期限 (valid_to) は開始 (valid_from) より後にしてください');
                return;
            }
        }
        // 2 段階 shake redesign (2026-05-16、 PR 6): 統一コード方式の validation。
        if (codeMode === 'unified') {
            if (!unifiedCode.trim()) {
                toastify('error', '統一コード方式ではクーポンコードが必須です');
                return;
            }
            if (unifiedCode.length > 64) {
                toastify('error', 'クーポンコードは 64 文字以内で入力してください');
                return;
            }
            if (maxDistributionsMode === 'limited') {
                const n = Number(maxDistributions);
                if (!Number.isInteger(n) || n < 0) {
                    toastify('error', '配布上限は 0 以上の整数を入力してください');
                    return;
                }
            }
        }

        const drawWeightNum = Number(drawWeight);
        if (drawWeight === '' || !Number.isInteger(drawWeightNum) || drawWeightNum < 0 || drawWeightNum > 1000) {
            toastify('error', '出やすさ (宿 QR) は 0〜1000 の整数で入力してください');
            return;
        }
        const reissueLimitNum = Number(reissueLimit);
        if (reissueLimit === '' || !Number.isInteger(reissueLimitNum) || reissueLimitNum < 0 || reissueLimitNum > 30) {
            toastify('error', 'コードの出し直しの上限は 0〜30 の整数で入力してください');
            return;
        }
        if (redeemMethod === 'code' && redeemUrl.trim() && !/^https?:\/\//i.test(redeemUrl.trim())) {
            toastify('error', 'リンク先は http:// または https:// で始まる URL を入力してください');
            return;
        }

        const descI18n: Record<string, string> | null =
            descJa.trim() || descEn.trim() || descZhTw.trim() || descKo.trim() ?
                {
                    ...(descJa.trim() && { ja: descJa.trim() }),
                    ...(descEn.trim() && { en: descEn.trim() }),
                    ...(descZhTw.trim() && { 'zh-TW': descZhTw.trim() }),
                    ...(descKo.trim() && { ko: descKo.trim() })
                } :
                null;
        // 民泊 PoC Step 3: クーポン名の訳 (手直し分)。日本語名が変わった保存ではサーバーが作り直す。
        const nameI18n: Record<string, string> = {
            ja: name.trim(),
            ...(nameEn.trim() && { en: nameEn.trim() }),
            ...(nameZhTw.trim() && { 'zh-TW': nameZhTw.trim() }),
            ...(nameKo.trim() && { ko: nameKo.trim() })
        };

        // 2 段階 shake redesign (2026-05-16、 PR 6): code_mode + unified_code + max_distributions の組み立て。
        // - unified: unified_code 必須、 max_distributions は unlimited なら null
        // - unique_per_distribution: 両方 null 強制 (= backend で validation あり、 client でも事前に空に)
        const codeModePayload = codeMode === 'unified' ?
            {
                code_mode: 'unified',
                unified_code: unifiedCode.trim(),
                max_distributions: maxDistributionsMode === 'unlimited' ? null : Number(maxDistributions)
            } :
            {
                code_mode: 'unique_per_distribution',
                unified_code: null,
                max_distributions: null
            };

        const payload: any = {
            fortune: fortune === FORTUNE_COMMON_SENTINEL ? null : Number(fortune),
            name: name.trim(),
            description_i18n: descI18n,
            name_i18n: nameI18n,
            valid_from: localInputToIso(validFrom),
            valid_to: localInputToIso(validTo),
            is_active: isActive,
            // クーポン画像 (2026-07-02): '' = null (= 画像削除 / 未設定 → enduser は motif 表示)。
            image_url: imageUrl || null,
            // 民泊 PoC Step 2: 宿 QR 経由の抽選での出やすさ (通常の GPS 抽選には影響しない)。
            draw_weight: drawWeightNum,
            // §47: 卓上 QR / 宿 QR での出し方・使い方・見出し・コードの出し直しの上限。
            facility_mode: facilityMode,
            redeem_method: redeemMethod,
            redeem_url: redeemMethod === 'code' ? (redeemUrl.trim() || null) : null,
            use_timing: useTiming === USE_TIMING_NONE ? null : useTiming,
            reissue_limit: reissueLimitNum,
            show_timing: showTiming,
            // 2 段階 shake redesign (2026-05-16、 PR 6): is_upgrade_only を送信。
            // backend で boolean 化される (= 通常 shake から除外する flag)。
            is_upgrade_only: kind === 'upgrade',
            ...codeModePayload,
            // Phase 3 PR 10 (2026-05-15): upgrade target、'' = null で送る (= 焼き直し連携 off)。
            // 自己参照 (= 元 = upgrade) は backend が許容するが UX 上ほぼ意味ない、
            // UI 側では同 template の選択肢には自分を含めない (= 後の Select で filter)。
            // 種別 = upgrade 時は UI state に関わらず強制 null (= chain 禁止の明示)。
            upgrade_target_template_id: kind === 'upgrade' || upgradeTargetTemplateId === '' ?
                null :
                Number(upgradeTargetTemplateId)
        };

        if (mode === 'create') {
            payload.landmark_id = Number(landmarkId);
        }

        setSubmitting(true);
        try {
            const res = mode === 'create' ?
                await flowResult(createTemplate(payload)) :
                await flowResult(updateTemplate({ id: template.id, data: payload }));

            if (res?.ok) {
                toastify('success', mode === 'create' ? '作成しました' : '更新しました');
                // 民泊 PoC Step 3: 自動翻訳の結果を知らせる (失敗しても保存は成功している)。
                const translation = (res.data as any)?.translation;
                if (translation?.attempted && translation.ok) {
                    toastify('success', '英語・繁体字・韓国語の訳を自動で作りました');
                } else if (translation?.attempted && !translation.ok) {
                    toastify('warn', '訳の自動作成に失敗しました (保存はできています)。外国語の画面では英語か日本語で表示されます。「翻訳を作り直す」で再試行できます');
                }
                onOpenChange(false);
                onSuccess?.();
            } else {
                toastify('error', res?.message || '保存に失敗しました');
            }
        } finally {
            setSubmitting(false);
        }
    };

    // 民泊 PoC Step 3: 「翻訳を作り直す」。今 DB に保存されている日本語から作り直す (未保存の編集は反映しない)。
    const handleRetranslate = async () => {
        if (!template?.id) return;
        setRetranslating(true);
        try {
            const res: any = await flowResult(retranslateTemplate(template.id));
            const data = res?.data;
            if (res?.ok && data?.translation?.ok) {
                const nameI18n = data.name_i18n ?? {};
                const descI18n = data.description_i18n ?? {};
                setNameEn(nameI18n.en ?? '');
                setNameZhTw(nameI18n['zh-TW'] ?? '');
                setNameKo(nameI18n.ko ?? '');
                setDescEn(descI18n.en ?? '');
                setDescZhTw(descI18n['zh-TW'] ?? '');
                setDescKo(descI18n.ko ?? '');
                toastify('success', '訳を作り直しました');
                onSuccess?.();
            } else {
                toastify('error', '訳の作り直しに失敗しました。時間をおいてもう一度お試しください (今の訳はそのまま残っています)');
            }
        } finally {
            setRetranslating(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className='w-[600px] sm:max-w-[600px] overflow-y-auto'>
                <SheetHeader>
                    <SheetTitle>
                        {mode === 'create' ? 'クーポンテンプレート新規作成' : 'クーポンテンプレート編集'}
                    </SheetTitle>
                    <SheetDescription>
                        landmark + fortune ごとに 1 件 = 1 テンプレート。
                        コード本体 (POS 登録済み文字列) は作成後に「コード入稿」 で batch 入稿。
                    </SheetDescription>
                </SheetHeader>

                <div className='space-y-4 py-4'>
                    <div className='rounded-md border border-gray-200 bg-gray-50 p-3'>
                        <Label>種別</Label>
                        <RadioGroup
                            className='mt-2'
                            value={kind}
                            onValueChange={(v) => {
                                const next = v as CouponTemplateKind;
                                setKind(next);
                                // upgrade に切替えた瞬間に target 選択をクリア (= chain 禁止の即時反映)。
                                if (next === 'upgrade') setUpgradeTargetTemplateId('');
                            }}
                        >
                            <div className='flex items-start space-x-2'>
                                <RadioGroupItem value='normal' id='kind-normal' className='mt-1' />
                                <Label htmlFor='kind-normal' className='font-normal cursor-pointer'>
                                    <span className='font-medium'>通常クーポン</span>
                                    <span className='block text-xs text-gray-500 mt-0.5'>
                                        shake で配布されるクーポン。レビュー特典で別クーポンに切替える設定が可能。
                                    </span>
                                </Label>
                            </div>
                            <div className='flex items-start space-x-2'>
                                <RadioGroupItem value='upgrade' id='kind-upgrade' className='mt-1' />
                                <Label htmlFor='kind-upgrade' className='font-normal cursor-pointer'>
                                    <span className='font-medium'>アップグレードクーポン</span>
                                    <span className='block text-xs text-gray-500 mt-0.5'>
                                        レビュー送信後の 2 回目シェイクで配布されるクーポン。
                                        通常シェイクでは当選しません (= 切替先専用)。
                                    </span>
                                </Label>
                            </div>
                        </RadioGroup>
                    </div>

                    {/* 2 段階 shake redesign (2026-05-16、 PR 6): コード方式 radio。
                        新規作成時のデフォルトは「統一コード方式」 (= U5)。 編集時は DB 値を反映。
                        2026-07-07 (pitfall batch P1-5): 配布実績がある template は方式変更を lock
                        (= 変更すると distribution_count / batch_codes の整合性が崩れるため)。 */}
                    <div className='rounded-md border border-gray-200 bg-gray-50 p-3'>
                        <Label>コード方式</Label>
                        <RadioGroup
                            className='mt-2'
                            value={codeMode}
                            onValueChange={(v) => {
                                if (codeModeLocked) return;
                                setCodeMode(v as CouponCodeMode);
                            }}
                        >
                            <div className='flex items-start space-x-2'>
                                <RadioGroupItem value='unified' id='code-mode-unified' className='mt-1' disabled={codeModeLocked} />
                                <Label htmlFor='code-mode-unified' className='font-normal cursor-pointer'>
                                    <span className='font-medium'>統一コード方式</span>
                                    <span className='ml-1 text-xs text-emerald-700'>★初期選択</span>
                                    <span className='block text-xs text-gray-500 mt-0.5'>
                                        同じコードを期間 / 配布上限内で全 user に配布。
                                        1 つのコードを店頭で確認する運用に最適。
                                    </span>
                                </Label>
                            </div>
                            <div className='flex items-start space-x-2'>
                                <RadioGroupItem value='unique_per_distribution' id='code-mode-unique' className='mt-1' disabled={codeModeLocked} />
                                <Label htmlFor='code-mode-unique' className='font-normal cursor-pointer'>
                                    <span className='font-medium'>個別コード方式</span>
                                    <span className='block text-xs text-gray-500 mt-0.5'>
                                        1 user 1 コード (= POS で 1 枚 1 枚追跡、 スクラッチ式)。
                                        作成後にコード一覧を投入 (CSV / 直接入力)。
                                    </span>
                                </Label>
                            </div>
                        </RadioGroup>
                        {codeModeLocked && (
                            <p className='mt-2 text-xs text-gray-600'>
                                🔒 配布実績があるため、コード方式は変更できません。方式を変えたい場合は新しいテンプレートを作成してください。
                            </p>
                        )}
                        {!codeModeLocked && mode === 'edit' && template?.code_mode &&
                            String(template.code_mode) !== codeMode && (
                            <p className='mt-2 text-xs text-amber-700'>
                                {`⚠️ コード方式を変更します (= 元: ${String(template.code_mode)} → 新: ${codeMode})。 既存の配布データ (= 個別 code 一覧 / 統一 distribution_count) は方式変更後に整合性が崩れる可能性があるので、 本番運用中の template では変更を避けてください。`}
                            </p>
                        )}
                    </div>

                    {/* 統一コード方式の時のみ表示: コード入力 + 配布上限 */}
                    {codeMode === 'unified' && (
                        <div className='rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-3'>
                            <div>
                                <Label>
                                    統一クーポンコード
                                    <span className='text-red-500'>*</span>
                                </Label>
                                <Input
                                    value={unifiedCode}
                                    onChange={e => setUnifiedCode(e.target.value)}
                                    placeholder='例: AA2026'
                                    maxLength={64}
                                />
                                <p className='text-xs text-gray-500 mt-1'>
                                    全 user に配布される同一のクーポンコード文字列 (= 1-64 文字)。
                                </p>
                            </div>

                            <div>
                                <Label>配布上限</Label>
                                <RadioGroup
                                    className='mt-2'
                                    value={maxDistributionsMode}
                                    onValueChange={v => setMaxDistributionsMode(v as 'limited' | 'unlimited')}
                                >
                                    <div className='flex items-center space-x-2'>
                                        <RadioGroupItem value='limited' id='max-dist-limited' />
                                        <Label htmlFor='max-dist-limited' className='font-normal cursor-pointer'>
                                            上限あり
                                        </Label>
                                        {maxDistributionsMode === 'limited' && (
                                            <>
                                                <Input
                                                    type='number'
                                                    min={0}
                                                    value={maxDistributions}
                                                    onChange={e => setMaxDistributions(e.target.value)}
                                                    className='w-24 ml-2'
                                                />
                                                <span className='text-sm text-gray-600'>枚</span>
                                            </>
                                        )}
                                    </div>
                                    <div className='flex items-center space-x-2'>
                                        <RadioGroupItem value='unlimited' id='max-dist-unlimited' />
                                        <Label htmlFor='max-dist-unlimited' className='font-normal cursor-pointer'>
                                            無制限
                                        </Label>
                                    </div>
                                </RadioGroup>
                                <p className='text-xs text-gray-500 mt-1'>
                                    上限に達したら配布停止 (= シェイク時に在庫切れ表示)。 無制限は期間 (有効期限) で制御。
                                </p>
                                {mode === 'edit' && typeof template?.distribution_count === 'number' && (
                                    <p className='text-xs text-emerald-700 mt-1'>
                                        {'現在の配布数: '}
                                        {template.distribution_count}
                                        {template.max_distributions != null && ` / ${template.max_distributions}`}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {codeMode === 'unique_per_distribution' && (
                        <div className='rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900'>
                            個別コード方式: テンプレート保存後に「コード入稿」 (↑ icon) から CSV / 直接入力でコードを投入してください。
                            1 件のコードは 1 user にのみ配布されます。
                        </div>
                    )}

                    <div>
                        <Label>
                            紐付け landmark
                            <span className='text-red-500'>*</span>
                        </Label>
                        <Select
                            value={landmarkId}
                            onValueChange={setLandmarkId}
                            disabled={mode === 'edit'}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder='landmark を選択' />
                            </SelectTrigger>
                            <SelectContent>
                                {landmarks.map(lm => (
                                    <SelectItem key={lm.id} value={String(lm.id)}>
                                        {lm.name}
                                        {' '}
                                        (#
                                        {lm.id}
                                        )
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {mode === 'edit' && (
                            <p className='text-xs text-amber-700 mt-1'>
                                landmark 紐付けは作成後変更不可 (= 別 template として作り直してください)
                            </p>
                        )}
                    </div>

                    <div>
                        <Label>fortune (= 排出階層)</Label>
                        <Select value={fortune} onValueChange={setFortune}>
                            <SelectTrigger>
                                <SelectValue placeholder='選択してください' />
                            </SelectTrigger>
                            <SelectContent>
                                {FORTUNE_OPTIONS.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className='text-xs text-gray-500 mt-1'>
                            「共通プール」 にすると fortune に関わらず draw 時の fallback として使われる。
                            fortune 別 template があれば fortune 一致が優先される。
                        </p>
                    </div>

                    <div>
                        <Label>
                            名称 (= 特典名。CMS 一覧と enduser の特典詳細・店頭利用シートに表示)
                            <span className='text-red-500'>*</span>
                        </Label>
                        <Input
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder='例: 2026夏キャンペーン 大吉用'
                            maxLength={128}
                        />
                        <p className='text-xs text-gray-500 mt-1'>
                            日本語で入力。一覧 / sheet タイトルに加え、enduser の特典詳細・店頭利用シートに特典名として表示される。
                            保存すると英語・繁体字・韓国語の訳を自動で作る (下の「翻訳」欄)。
                        </p>
                    </div>

                    <div>
                        <Label>説明 ja (= enduser PWA の取得モーダルに表示)</Label>
                        <Textarea
                            value={descJa}
                            onChange={e => setDescJa(e.target.value)}
                            rows={2}
                            placeholder='例: 対象店舗で 10% off'
                        />
                        <p className='text-xs text-gray-500 mt-1'>
                            shake で取得した user に向けた説明文 (= 「対象店舗で 10% off」 等)。日本語で入力。
                            未入力なら表示なし。外国語は保存時に自動で訳す (下の「翻訳」欄)。
                        </p>
                    </div>

                    {/* 民泊 PoC Step 3 (2026-09-26): 自動翻訳の確認・手直し。 */}
                    <div className='border rounded-md p-3 space-y-3'>
                        <div className='flex items-center justify-between gap-2'>
                            <Label>翻訳 (英語・繁体字・韓国語 / 自動作成)</Label>
                            {mode === 'edit' && (
                                <Button type='button' variant='outline' size='sm' onClick={handleRetranslate} disabled={retranslating}>
                                    {retranslating ? '作成中…' : '翻訳を作り直す'}
                                </Button>
                            )}
                        </div>
                        <p className='text-xs text-gray-500'>
                            日本語の名前・説明を保存すると、AI が訳を作って入れる。ここで手直しして保存すれば、その訳が使われる。
                            ただし
                            <b>日本語を変えて保存すると、訳は作り直される (手直しは上書き)</b>
                            。
                            スマホの言語が英語・繁体字・韓国語の利用者にだけ表示され、訳が空の言語は英語 → 日本語の順に表示する。
                            店名・宿名は訳さずそのまま残す。
                        </p>
                        {[
                            { label: '英語 (en)', name: nameEn, setName: setNameEn, desc: descEn, setDesc: setDescEn },
                            { label: '繁体字 (zh-TW)', name: nameZhTw, setName: setNameZhTw, desc: descZhTw, setDesc: setDescZhTw },
                            { label: '韓国語 (ko)', name: nameKo, setName: setNameKo, desc: descKo, setDesc: setDescKo }
                        ].map(row => (
                            <div key={row.label} className='space-y-1'>
                                <p className='text-xs font-semibold text-gray-600'>{row.label}</p>
                                <Input
                                    value={row.name}
                                    onChange={e => row.setName(e.target.value)}
                                    placeholder='名前 (未作成)'
                                    maxLength={255}
                                />
                                <Textarea
                                    value={row.desc}
                                    onChange={e => row.setDesc(e.target.value)}
                                    rows={2}
                                    placeholder='説明 (未作成)'
                                />
                            </div>
                        ))}
                    </div>

                    {/* クーポン画像 (2026-07-02): ファイル選択 → 即時アップロード → プレビュー。
                        2026-09-26 役割変更: enduser ではカードを置き換えず、クーポン欄に特典の写真として表示される。
                        Spec: design/coupon-image-feature-spec-20260702.md */}
                    <div>
                        <Label>クーポン画像 (任意、特典の写真)</Label>
                        <div className='mt-2'>
                            {imageUrl ?
                                (
                                    <div className='flex items-start gap-3'>
                                        <img
                                            src={imageUrl}
                                            alt='クーポン画像プレビュー'
                                            className='w-36 h-36 object-contain rounded-md border border-gray-200 bg-gray-50'
                                        />
                                        <div className='flex flex-col gap-2'>
                                            <Button
                                                type='button'
                                                variant='outline'
                                                onClick={() => imageInputRef.current?.click()}
                                                disabled={uploadingImage}
                                            >
                                                {uploadingImage ? 'アップロード中...' : '画像を変更'}
                                            </Button>
                                            <Button
                                                type='button'
                                                variant='outline'
                                                className='text-red-600'
                                                onClick={() => setImageUrl('')}
                                                disabled={uploadingImage}
                                            >
                                                画像を削除
                                            </Button>
                                        </div>
                                    </div>
                                ) :
                                (
                                    <Button
                                        type='button'
                                        variant='outline'
                                        onClick={() => imageInputRef.current?.click()}
                                        disabled={uploadingImage}
                                    >
                                        {uploadingImage ? 'アップロード中...' : '画像を選択'}
                                    </Button>
                                )}
                            <input
                                ref={imageInputRef}
                                type='file'
                                accept='image/jpeg,image/png'
                                className='hidden'
                                onChange={handleImageFileChange}
                            />
                        </div>
                        <p className='text-xs text-gray-500 mt-1'>
                            設定すると、このクーポンが当たった時に
                            <span className='font-medium'>クーポン欄 (コードの上) に特典の写真</span>
                            として表示されます。会計時にスタッフが見る確認画面にも出ます。
                            おみくじカードの見た目 (背景) はスポット編集の「カード背景画像」で設定します。
                            jpg / png、10MB まで。大きい画像は自動圧縮 (JPEG 変換、透過は保持されません)。
                            画像の差し替えは新規取得分から反映され、取得済みクーポンには影響しません。
                        </p>
                    </div>

                    <div className='grid grid-cols-2 gap-3'>
                        <div>
                            <Label>有効期間 開始 (任意)</Label>
                            <Input
                                type='datetime-local'
                                value={validFrom}
                                onChange={e => setValidFrom(e.target.value)}
                            />
                        </div>
                        <div>
                            <Label>有効期間 終了 (任意)</Label>
                            <Input
                                type='datetime-local'
                                value={validTo}
                                onChange={e => setValidTo(e.target.value)}
                            />
                        </div>
                    </div>
                    <div>
                        <Label>出やすさ (宿 QR)</Label>
                        <Input
                            type='text'
                            inputMode='numeric'
                            className='w-[120px]'
                            value={drawWeight}
                            onChange={e => setDrawWeight(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        />
                        <p className='text-xs text-gray-500 mt-1'>
                            宿の部屋 QR から引いた時に、この店が出る割合の重み (例: 3 と 1 なら おおむね 3 : 1)。
                            0 = 宿 QR では出さない。通常の (位置情報での) 抽選には影響しません。
                        </p>
                    </div>

                    <div className='rounded-md border border-gray-200 p-3 space-y-3'>
                        <p className='text-sm font-semibold'>卓上 QR / 宿 QR での出し方</p>
                        <p className='text-xs text-gray-500'>
                            QR から引いた時だけに効く設定です。通常の (位置情報での) 抽選には影響しません。
                        </p>
                        <div>
                            <Label>出し方</Label>
                            <Select value={facilityMode} onValueChange={v => setFacilityMode(v as 'random' | 'always')}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value='random'>ランダム (出やすさの割合で 1 つ選ぶ)</SelectItem>
                                    <SelectItem value='always'>必ず付ける (引くたびに、ランダムの特典と一緒に付く)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>使い方</Label>
                            <Select value={redeemMethod} onValueChange={v => setRedeemMethod(v as 'staff' | 'code')}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value='staff'>お店の人に見せる (右へスライドして使用)</SelectItem>
                                    <SelectItem value='code'>コードを入力して使う (他のアプリで使う割引など)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {redeemMethod === 'staff' && (
                            <div>
                                <Label>お店の人に見せるとき</Label>
                                <Select value={showTiming} onValueChange={v => setShowTiming(v as 'pay' | 'order')}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value='pay'>お会計のとき (割引など)</SelectItem>
                                        <SelectItem value='order'>注文のとき (1 品サービスなど)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        {redeemMethod === 'code' && (
                            <div>
                                <Label>リンク先 (任意)</Label>
                                <Input
                                    type='url'
                                    placeholder='https://...'
                                    value={redeemUrl}
                                    onChange={e => setRedeemUrl(e.target.value)}
                                />
                                <p className='text-xs text-gray-500 mt-1'>
                                    画面の「コピーして開く」ボタンで開く先 (アプリのページなど)。空ならコピーだけ。
                                </p>
                            </div>
                        )}
                        <div>
                            <Label>いつ使うか (画面の見出し)</Label>
                            <Select value={useTiming} onValueChange={setUseTiming}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={USE_TIMING_NONE}>このお店で (いつでも使える)</SelectItem>
                                    <SelectItem value='today'>今日の帰りに</SelectItem>
                                    <SelectItem value='next_visit'>次に来たときに (受け取った日は使えない)</SelectItem>
                                    <SelectItem value='nearby'>近くのお店で (いつでも使える)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {facilityMode === 'always' && (
                            <div>
                                <Label>コードの出し直しの上限 (回)</Label>
                                <Input
                                    type='text'
                                    inputMode='numeric'
                                    className='w-[120px]'
                                    value={reissueLimit}
                                    onChange={e => setReissueLimit(e.target.value.replace(/\D/g, '').slice(0, 2))}
                                />
                                <p className='text-xs text-gray-500 mt-1'>
                                    受け取った日と別の日に特典を開いた時、新しいコードを出す回数の上限 (1 日 1 回まで)。
                                    上限の後は最後のコードのまま。0 = 出し直さない。
                                </p>
                            </div>
                        )}
                    </div>

                    <p className='text-xs text-gray-500'>
                        期間外の template は draw 対象から除外される。
                        個別 code の valid_to は「コード入稿」 で override 可。
                    </p>

                    <div className={kind === 'upgrade' ? 'opacity-50 pointer-events-none' : ''}>
                        <Label>レビュー後に切替えるクーポン (任意)</Label>
                        <Select
                            value={upgradeTargetTemplateId === '' ? '__none__' : upgradeTargetTemplateId}
                            onValueChange={v => setUpgradeTargetTemplateId(v === '__none__' ? '' : v)}
                            disabled={kind === 'upgrade'}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder='選択してください' />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value='__none__'>切替なし (= レビューを送っても元のクーポンのまま)</SelectItem>
                                {(availableUpgradeTargets ?? [])
                                    .filter(t => template?.id == null || Number(t.id) !== Number(template.id))
                                    .map(t => (
                                        <SelectItem key={t.id} value={String(t.id)}>
                                            #
                                            {t.id}
                                            {' — '}
                                            {t.name}
                                        </SelectItem>
                                    ))}
                            </SelectContent>
                        </Select>
                        {kind === 'upgrade' ?
                            (
                                <p className='text-xs text-gray-500 mt-1'>
                                    アップグレードクーポンは切替先を持ちません (= 種別「通常クーポン」 でのみ設定可能)。
                                </p>
                            ) :
                            (
                                <p className='text-xs text-gray-500 mt-1'>
                                    利用者がアプリでレビュー (星評価) を送信した時に、このクーポンが
                                    ここで選んだクーポンに切替わります。
                                    <br />
                                    例: このクーポン = 通常 10% off → 切替先 = レビュー特典 20% off
                                    <br />
                                    ※ 同じ事業者が登録したクーポンのみ選択できます。
                                </p>
                            )}
                    </div>

                    <div className='flex items-center gap-2'>
                        <input
                            type='checkbox'
                            checked={isActive}
                            onChange={e => setIsActive(e.target.checked)}
                            id='coupon_template_is_active'
                        />
                        <Label htmlFor='coupon_template_is_active'>
                            is_active (= draw 対象、false で一時停止)
                        </Label>
                    </div>
                </div>

                <SheetFooter>
                    <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                        キャンセル
                    </Button>
                    <Button type='button' variant='active' onClick={handleSubmit} disabled={submitting}>
                        {mode === 'create' ? '作成' : '更新'}
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
});
